import { formatDate } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ClassificationTypeDto, ClassificationTypesLexiClient, ClassificationValueDto, ClassificationValuesLexiClient, ClassificationsLexiClient, CreateClassificationDto, CsvImportResultDto, ObjectType, PartenaireDto, PartenaireType, PartenairesLexiClient, Permissions, ProfilPartenaireDto, ProfilPartenairesLexiClient } from '@lexi-clients/lexi';
import { DxDataGridComponent, DxTreeListComponent } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { ExportingEvent } from 'devextreme/ui/data_grid';
import notify from "devextreme/ui/notify";
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { NotificationType } from 'lexi-angular/src/app/modules/shared/references/references';
import { AuthService } from 'lexi-angular/src/app/settings/auth.service';
import { lastValueFrom } from 'rxjs';
import { PartenaireCategorieEnum, partenaireCategories, partenaireCategoriesLibellesMap } from '../../../models/enums/partenaireCategorie';
import { DownloadService } from '../../../services/download.service';
import { DxDataSourceService } from '../../../shared/services/dx-data-source.service';
import { PartenaireNewFormComponent } from '../partenaire-new-form/partenaire-new-form.component';
import dxTreeList, { SelectionChangedEvent as TreeListSelectionChangedEvent } from 'devextreme/ui/tree_list';
import { confirm } from 'devextreme/ui/dialog';

const showClassificationsStorageKey = "showClassificationsPartenaireList";
const isSelectionRecursivePartenaireList = "isSelectionRecursivePartenaireList";

@Component({
    selector: 'app-partenaire-list',
    templateUrl: './partenaire-list.component.html',
    styleUrls: ['./partenaire-list.component.scss'],
    standalone: false
})
export class PartenaireListComponent implements OnInit {
  private _treeList: DxTreeListComponent;
  get treeList() { return this._treeList; }
  @ViewChild('treeList') set treeList(value: DxTreeListComponent) {
    this._treeList = value;
    if (value) {
      value.instance.updateDimensions();
      value.instance.element().style.height = "calc(100vh - 190px)";
    }
  }

  @ViewChild('gridContainer', { static: false }) dataGrid: DxDataGridComponent;
  @ViewChild(PartenaireNewFormComponent, { static: false }) partenaireNewForm: PartenaireNewFormComponent;

  dataSource: CustomStore;
  initialized = false;
  showImportCsvPopup = false;
  pattern: string;
  fichierCsv: File[] = [];
  importCsvResultMsg: string;
  importCsvResultMsg2: string;
  importHasFailed = false;
  showLoader = false;
  partenaireCategorie = PartenaireCategorieEnum.clients;
  partenaireCategories = partenaireCategories;
  isPartenaireCategorieFige: boolean = true;
  recuperationTemplateEncours = false;
  showLegende = false;
  estClient: boolean | null;
  estFournisseur: boolean | null;
  estPartenaire: boolean | null;
  grilleStorageKey = "grilles_partenaire_list_" + this.partenaireCategorieIntitule;
  isAddPartenairePopupOpened: boolean = false;
  profilPartenaireDataSource: ProfilPartenaireDto[] = [];
  canAdministrerClientsPermission : boolean = false;

  // Classification
  selectedClassificationTypeId: number | null;
  readonly selectedClassificationTypeIdStorageKey = "SelectionPartenairesClassificationTypeId";
  classificationTypes: ClassificationTypeDto[];
  classificationValues: ClassificationValueDto[];
  selectedClassificationValues: ClassificationValueDto[];
  showClassifications = false;
  classificationValueIds: number[];
  sansClassificationValue = false;
  isSelectionRecursive = true;
  objectType = ObjectType;
  constructor(
    private readonly partenairesLexiClient: PartenairesLexiClient,
    private readonly dxDataSourceService: DxDataSourceService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly downloadService: DownloadService,
    private readonly profilPartenairesLexiClient: ProfilPartenairesLexiClient,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly classificationTypesLexiClient: ClassificationTypesLexiClient,
    private readonly classificationValuesLexiClient: ClassificationValuesLexiClient,
    private readonly classificationsLexiClient: ClassificationsLexiClient,
  ) { }

  ngOnInit() {
    this.activatedRoute.url.subscribe(async (urlSegment) => {
      const partenaireCategorieFromUrl: string = urlSegment[0].path;
      switch (partenaireCategorieFromUrl) {
        case "clients": {
          this.partenaireCategorie = PartenaireCategorieEnum.clients;
          this.isPartenaireCategorieFige = true;
          break;
        }
        case "fournisseurs": {
          this.partenaireCategorie = PartenaireCategorieEnum.fournisseurs;
          this.isPartenaireCategorieFige = true;
          break;
        }
        case "partenaires": {
          this.partenaireCategorie = PartenaireCategorieEnum.internes;
          this.isPartenaireCategorieFige = false;
          this.partenaireCategories = partenaireCategories.filter(x => x.enumId !== PartenaireCategorieEnum.clients && x.enumId !== PartenaireCategorieEnum.fournisseurs);
          break;
        }
      }

      if (!this.initialized) {
        this.profilPartenaireDataSource = await lastValueFrom(this.profilPartenairesLexiClient.getAll());
        this.classificationTypes = await this.setClassificationTypes();

        // LocalStorage pour la classification
        const storedClassificationType = localStorage.getItem(this.selectedClassificationTypeIdStorageKey);
        if (storedClassificationType != null && !isNaN(parseInt(storedClassificationType, 10))) {
          this.selectedClassificationTypeId = parseInt(storedClassificationType, 10);
        }

        // LocalStorage pour showClassifications et isSelectionRecursive
        if (localStorage.getItem(showClassificationsStorageKey) === 'true') {
          this.showClassifications = true;
        }
        if (localStorage.getItem(isSelectionRecursivePartenaireList)) {
          this.isSelectionRecursive = true;
        }

        await this.setClassificationValues();
        this.setDataSource();
        this.initialized = true;
      }
      this.canAdministrerClientsPermission = this.authService.securityUserisGrantedWith(Permissions.canAdministrerClients);
    });
  }

  get partenaireCategorieIntitule(): string {
    return partenaireCategoriesLibellesMap.get(this.partenaireCategorie);
  }

  setDataSource(sansClassification: boolean = false) {
    this.sansClassificationValue = sansClassification;
    this.estClient = this.partenaireCategorie == PartenaireCategorieEnum.clients ? true : null;
    this.estFournisseur = this.partenaireCategorie == PartenaireCategorieEnum.fournisseurs ? true : null;
    const partenaireType: PartenaireType | null = this.partenaireCategorie == PartenaireCategorieEnum.internes ? PartenaireType.interne
      : this.partenaireCategorie == PartenaireCategorieEnum.externes ? PartenaireType.externe
      : null;
    this.dataSource = this.dxDataSourceService.getPartenaireCustomStore(this.estClient, this.estFournisseur, partenaireType, this.pattern, this.selectedClassificationTypeId, this.classificationValueIds?.toString(), sansClassification);
  }

  clearDatagridFilters() {
    this.dataGrid?.instance.clearFilter();
  }

  async importDataFromCsv() {
    this.resetImportResultMsg();
    this.showLoader = true;
    try {
      const response: CsvImportResultDto = await lastValueFrom(this.partenairesLexiClient.importPartenairesFromCsv(true, this.fichierCsv[0]));

      // Erreur
      if (response.hasFailed) {
        this.importHasFailed = true;
        if (response.errorMessage != null) {
          this.importCsvResultMsg = response.errorMessage;
        }

        else {
          if (response.hasWrongHeadersCount) {
            this.importCsvResultMsg = "Le nombre de colonnes est incorrecte, il y a plus de colonnes que celles attendues.";
          }

          else {
            this.importCsvResultMsg = `Les colonnes suivantes sont invalides: ${response.invalidHeaders.join(', ')}.`;
          }

          if (response.expectedHeaders != null) {
            this.importCsvResultMsg2 = `Colonnes attendues: (${response.expectedHeaders.join(', ')})`;
          }
        }
      }

      else if (response.itemsOnError.length > 0) {
        let errorMessage = "";
        response.itemsOnError.forEach(x => errorMessage += `${x}\n`);
        this.importCsvResultMsg = `Certaines éléments n'ont pas pu être importées. Le détail a été copié dans le presse papier`;
        this.importCsvResultMsg2 = "Importation incomplète";
        navigator.clipboard.writeText(errorMessage).then().catch(e => console.error(e));
        this.setDataSource();
      }

      // Succès
      else {
        this.importHasFailed = false;
        this.setDataSource();
        this.importCsvResultMsg = `${response.nbItemsCreated} élément(s) ont été créé(s), ${response.nbItemsUpdated} élément(s) ont été mis à jour.\nL'importation s'est terminée.`;
      }

      this.fichierCsv = [];
    }
    finally {
      this.showLoader = false;
    }
  }

  cancelImportCsv() {
    this.showImportCsvPopup = false;
    this.resetImportResultMsg();
    this.fichierCsv = [];
  }

  resetImportResultMsg() {
    this.importCsvResultMsg = null;
    this.importCsvResultMsg2 = null;
  }

  onRowPrepared(e) {
    if (!e.data?.actif && e.rowType == "data") {
      e.rowElement.classList.add("row-bloque");
    }
  }

  onPartenaireCategoryChanged(e: { value: PartenaireCategorieEnum }) {
    this.partenaireCategorie = e.value;
    this.setDataSource();
  }

  async downloadTemplate() {
    try {
      this.recuperationTemplateEncours = true;
      const response = await lastValueFrom(this.partenairesLexiClient.getTemplateImportCsv('response'));
      this.downloadService.directDownloadFile(response, 'csv');
    }
    finally {
      this.recuperationTemplateEncours = false;
    }
  }

  onExporting(e: ExportingEvent) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Partenaires');

    exportDataGrid({
      component: e.component,
      worksheet,
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell.rowType === 'data') {

          // Code
          if (gridCell.column.caption === 'Code' && gridCell.value == null) {
            excelCell.value = gridCell.data.codeBoClient ?? gridCell.data.codeBo;
          }
        }
      },
    }).then(() => {
      const date = formatDate((new Date), "yyyyMMdd-HHmm", "fr-FR");
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${date}-${this.partenaireCategorieIntitule}.xlsx`);
      });
    });
  }

  addPartenaire() {
    this.isAddPartenairePopupOpened = true;
  };

  onClickSaveNewContact() {
    this.isAddPartenairePopupOpened = false;
  }

  onClickSaveNewPartenaire = async () => {
    const newPartenaire: PartenaireDto = this.partenaireNewForm.getNewPartenaireData();
    if(!newPartenaire) return;

    if (newPartenaire.intitule == null || newPartenaire.intitule.trim() === '') {
      notify({ closeOnClick: true, message: "L'intitulé est obligatoire." }, NotificationType.Error);
      return;
    }
    
    try {
      newPartenaire.estFournisseur ??= false;
      await lastValueFrom(this.partenairesLexiClient.create(newPartenaire));
      notify({ closeOnClick: true, message: `Nouveau partenaire "${newPartenaire.codeBo} ${newPartenaire.intitule}" créé.` }, NotificationType.Success);
    }
    finally {
      this.setDataSource();
    }
  }

  navigateToDoublons() {
    this.router.navigate(['/clients/doublons']);
  }

  classificationTypeDisplayExpr = (type: ClassificationTypeDto) => {
    return type && type.codeBo + " - " + type.intitule;
  }

  onClassificationTypeChanged = async (e: any) => {
    localStorage.removeItem(this.selectedClassificationTypeIdStorageKey);
    if (e?.item != null) {
      this.selectedClassificationTypeId = e?.item?.id;
      localStorage.setItem(this.selectedClassificationTypeIdStorageKey, JSON.stringify(this.selectedClassificationTypeId))
    } else {
      this.selectedClassificationTypeId = null;
    }

    // Set des partenaires et des classificationValues
    this.setDataSource(this.sansClassificationValue);
    this.clearTreeListFilters();
    await this.setClassificationValues();
  }

  async setClassificationTypes() {
    let classificationTypes: ClassificationTypeDto[] = await lastValueFrom(this.classificationTypesLexiClient.getByObjectType(ObjectType.partenaire));
    
    return classificationTypes.sort((a, b) => {
      return (a.intitule || '').localeCompare(b.intitule || '', 'fr', { sensitivity: 'base' });
    });
  }

  async setClassificationValues() {
    this.classificationValues = this.selectedClassificationTypeId == null ? []
      : await lastValueFrom(this.classificationValuesLexiClient.getByClassificationTypeId(this.selectedClassificationTypeId));
  }

  async expandRecursively(dxTreeList: dxTreeList<any, any>, rowKey: number, rowIndex: number) {
    try {
      await dxTreeList.expandRow(rowKey);
      const children = dxTreeList.getVisibleRows()?.find(x => x.rowIndex == rowIndex)?.node?.children;
      if (children) {
        children.forEach(async (element) => {
          const index = dxTreeList.getRowIndexByKey(element.key);
          await this.expandRecursively(dxTreeList, element.key, index);
        });
      }
    }
    catch { }
  }

  async onClassificationValueSelectionChanged(e: TreeListSelectionChangedEvent) {
    // On ne filtre pas si la désélection vient du fait d'avoir mis le filtre 'Sans classification uniquement'
    if (this.sansClassificationValue && e.currentSelectedRowKeys?.length < 1) return;
    const rows = e.component.getVisibleRows();
    for (const element of rows.filter(x => x.isSelected && !x.isExpanded)) {
      await this.expandRecursively(e.component, element.key, element.rowIndex);
    }

    await e.component.refresh();
    this.classificationValueIds = e.component.getSelectedRowKeys('all') as number[];
    setTimeout(() => {
      this.setDataSource(false);
    }, 100);
  }

  onToggleClassificationDisplay() {
    this.treeList?.instance?.clearSelection();
    this.dataGrid?.instance.clearSelection();
    this.classificationValueIds = null;
    this.showClassifications = !this.showClassifications;
    if (this.showClassifications) {
      localStorage.setItem(showClassificationsStorageKey, "true");
    }
    else {
      setTimeout(() => {
        this.setDataSource();
      }, 100);

      localStorage.removeItem(showClassificationsStorageKey);
    }
  }

  async onToggleIsSelectionRecursive() {
    if (this.isSelectionRecursive) {
      localStorage.setItem(isSelectionRecursivePartenaireList, "true");
    }
    else {
      localStorage.removeItem(isSelectionRecursivePartenaireList);
    }

    await this.treeList?.instance.refresh();
    this.classificationValueIds = this.treeList?.instance.getSelectedRowKeys('all') as number[];
    setTimeout(() => {
      this.setDataSource(false);
    }, 100);
  }

  clearTreeListFilters() {
    this.treeList?.instance?.clearFilter();
    this.treeList?.instance?.clearSelection();
    this.treeList?.instance?.clearSorting();
    this.classificationValueIds = null;
  }

  onAddPartenaireIntoClassification = async (e: { itemData: PartenaireDto, component: any, toIndex: number }) => {
    if (this.dataGrid?.instance == null) return;
    const rows = e.component.getVisibleRows();
    const classification: ClassificationValueDto = rows?.find(x => x.rowIndex == e.toIndex)?.data;
    let partenaires: PartenaireDto[] = this.dataGrid.instance.getSelectedRowsData();
    if ((partenaires == null || partenaires.length == 0) && e.itemData != null) {
      partenaires = [e.itemData];
    }
    const s = partenaires?.length > 1 ? "s" : "";
    if (partenaires?.length > 0 && classification != null) {
      const confirmed = await confirm(`Associer ${partenaires.length} partenaire${s} à la classification '${classification.intitule}'`, `Confirmation`);
      if (confirmed) {
        const dtos: CreateClassificationDto[] = partenaires.map(x => {
          return {
            itemId: x.id,
            classificationValueId: classification.id,
            type: ObjectType.partenaire,
          };
        });
        await lastValueFrom(this.classificationsLexiClient.create(dtos));
        notify({ closeOnClick: true, message: `Association${s} faite${s} avec succès.` }, NotificationType.Success);
        this.dataGrid.instance.clearSelection();
        setTimeout(() => {
          this.setDataSource(this.sansClassificationValue);
        }, 100);
      }
    }
  }
}
